import Box from '@mui/material/Box';
import ChatPanel from 'components/common/ChatPanel';
import Conversation from 'components/common/Conversation';
import { PrimaryBtn } from 'components/UI/Buttons';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetDemoClientMessagesQuery } from 'services/api/demochat';
import {
  useCreateRealtimeVoiceClientMutation,
  useExecuteToolMutation,
  useStartRealtimeVoiceChatMutation,
  useStoreMessageMutation,
} from 'services/api/realtimeVoice';
import { capitalizeFirstLetterOfEachWord } from 'utils/capitalize';
import getReCaptchaToken from 'utils/getReCaptchaToken';

const OPENAI_REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

type RealtimeMessage = {
  type: string;
  event_id?: string;
  item_id?: string;
  transcript?: string;
  name?: string;
  arguments?: string;
  call_id?: string;
  response?: {
    output: {
      id: string;
      type: 'message' | 'function_call';
      name?: string;
      arguments?: string;
      call_id?: string;
      content?: { type: 'audio' | 'text'; transcript: string }[];
      role: 'assistant' | 'system' | 'user';
    }[];
    usage: {
      total_tokens: number;
      input_tokens: number;
      output_tokens: number;
      input_token_details: {
        text_tokens: number;
        audio_tokens: number;
        cached_tokens: number;
        cached_tokens_details: { text_tokens: number; audio_tokens: number };
      };
      output_token_details: { text_tokens: number; audio_tokens: number };
    };
  };
};

const RealtimeVoice = () => {
  const { companyIdentifier } = useParams<{ companyIdentifier: string }>();

  const [sessionState, setSessionState] = useState<'idle' | 'connecting' | 'active'>('idle');
  const [activeClientId, setActiveClientId] = useState<string | null>(null);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [createRealtimeVoiceClient] = useCreateRealtimeVoiceClientMutation();
  const [startRealtimeVoiceChat] = useStartRealtimeVoiceChatMutation();
  const [storeMessage] = useStoreMessageMutation();
  const [executeTool] = useExecuteToolMutation();

  const { data: messages, refetch } = useGetDemoClientMessagesQuery(
    { clientPhoneIdentifier: activeClientId ?? '' },
    { skip: !activeClientId }
  );

  // Always-current refs for use inside async callbacks
  const storeMessageRef = useRef(storeMessage);
  storeMessageRef.current = storeMessage;
  const executeToolRef = useRef(executeTool);
  executeToolRef.current = executeTool;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const stopSession = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    peerConnectionRef.current?.close();
    peerConnectionRef.current = null;
    setSessionState('idle');
  };

  const startSession = async () => {
    // Clean up any existing session before starting a new one
    stopSession();
    setSessionState('connecting');

    const token = await getReCaptchaToken();
    const clientResult = await createRealtimeVoiceClient({
      companyIdentifier: companyIdentifier ?? '',
      recaptchaToken: token,
    }).unwrap();

    // clientId as closure variable — avoids React state timing issues
    const clientId = clientResult.id;
    setActiveClientId(clientId);

    const { ephemeralToken } = await startRealtimeVoiceChat({
      clientPhoneIdentifier: clientId,
    }).unwrap();

    const pc = new RTCPeerConnection();
    peerConnectionRef.current = pc;

    // Guard: ignore events from a PC that's no longer the active one
    const isCurrentPc = () => peerConnectionRef.current === pc;

    pc.ontrack = (event) => {
      if (!isCurrentPc()) return;
      const el = document.createElement('audio');
      [el.srcObject] = event.streams;
      el.autoplay = true;
      el.onpause = () => {
        if (isCurrentPc()) stopSession();
      };
      document.getElementById('realtime-voice-audio')?.replaceChildren(el);
    };

    pc.onconnectionstatechange = () => {
      if (!isCurrentPc()) return;
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        stopSession();
      }
    };

    const dataChannel = pc.createDataChannel('oai-events');

    dataChannel.addEventListener('message', (event) => {
      if (!isCurrentPc()) return;
      const message = JSON.parse(event.data as string) as RealtimeMessage;

      if (message.type === 'conversation.item.input_audio_transcription.completed') {
        storeMessageRef
          .current({
            message: { messageId: message.item_id, text: message.transcript ?? '', role: 'user' },
            clientPhoneIdentifier: clientId,
          })
          .then(() => refetchRef.current())
          .catch(console.error);
        return;
      }

      if (message.type === 'response.function_call_arguments.done') {
        executeToolRef
          .current({
            clientPhoneIdentifier: clientId,
            toolCallId: message.call_id ?? '',
            functionName: message.name ?? '',
            functionArguments: message.arguments ?? '',
          })
          .unwrap()
          .then(({ result }) => {
            if (!isCurrentPc()) return;
            dataChannel.send(
              JSON.stringify({
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: message.call_id,
                  output: JSON.stringify(result),
                },
              })
            );
            dataChannel.send(JSON.stringify({ type: 'response.create' }));
          })
          .catch(console.error);
        return;
      }

      if (message.type === 'response.done' && message.response) {
        const { output, usage } = message.response;
        output.forEach((item) => {
          if (item.type !== 'message') return;
          storeMessageRef
            .current({
              message: {
                messageId: item.id,
                text: item.content?.[0]?.transcript ?? '',
                role: item.role,
                AIUsage: usage,
              },
              clientPhoneIdentifier: clientId,
            })
            .then(() => refetchRef.current())
            .catch(console.error);
        });
      }
    });

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // If session was stopped while waiting for mic permission, clean up and bail
    if (!isCurrentPc()) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    streamRef.current = stream;
    stream.getTracks().forEach((track) => pc.addTransceiver(track, { direction: 'sendrecv' }));

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const fd = new FormData();
    fd.set('sdp', offer.sdp ?? '');
    const sdpResponse = await fetch(OPENAI_REALTIME_CALLS_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${ephemeralToken}` },
      body: fd,
    });

    if (!sdpResponse.ok) {
      stopSession();
      throw new Error(`OpenAI WebRTC error: ${sdpResponse.status}`);
    }

    if (!isCurrentPc()) return;

    await pc.setRemoteDescription({ type: 'answer', sdp: await sdpResponse.text() });
    setSessionState('active');
  };

  return (
    <ChatPanel>
      <ChatPanel.Header title={capitalizeFirstLetterOfEachWord(companyIdentifier ?? 'Demo Chat')} subtitle="En línea" />
      <ChatPanel.Body>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Conversation conversation={messages ?? []} isFilteredConversation isClientView fullHeight />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 3 }}>
            <div id="realtime-voice-audio" />
            {sessionState === 'active' && (
              <Box sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1 }}>🎙️ Ya puedes hablar</Box>
            )}
            {sessionState === 'idle' && (
              <PrimaryBtn
                onClick={() => {
                  startSession().catch((error) => {
                    console.error(error);
                    setSessionState('idle');
                  });
                }}
                sx={{ backgroundColor: '#fff', width: '300px' }}
              >
                Empezar nueva sesión
              </PrimaryBtn>
            )}
            {sessionState === 'connecting' && (
              <PrimaryBtn disabled sx={{ backgroundColor: '#fff', width: '300px', opacity: 0.6 }}>
                Conectando...
              </PrimaryBtn>
            )}
            {sessionState === 'active' && (
              <PrimaryBtn onClick={stopSession} sx={{ backgroundColor: '#fff', width: '300px' }}>
                Cerrar sesión
              </PrimaryBtn>
            )}
          </Box>
        </Box>
      </ChatPanel.Body>
    </ChatPanel>
  );
};

export default RealtimeVoice;
